import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

const { FiX, FiImage, FiCalendar, FiUpload, FiLoader, FiUsers } = FiIcons;

export default function MemoryModal({ memory, onClose, onSave, familyMembers = [] }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    imageUrl: '',
    tags: '',
    people: [] // Array of uuids
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (memory) {
      setFormData({
        ...memory,
        tags: memory.tags || '',
        people: memory.people || []
      });
    }
  }, [memory]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };

  const handleFileUpload = async (e) => {
    try {
        setUploading(true);
        const file = e.target.files[0];
        if (!file) return;

        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `${user.id}/${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('Erinnerungen')
            .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data } = supabase.storage.from('Erinnerungen').getPublicUrl(filePath);
        
        setFormData(prev => ({ ...prev, imageUrl: data.publicUrl }));
    } catch (error) {
        alert('Fehler beim Upload: ' + error.message);
    } finally {
        setUploading(false);
    }
  };

  const togglePerson = (id) => {
      setFormData(prev => {
          const current = prev.people || [];
          if (current.includes(id)) return { ...prev, people: current.filter(p => p !== id) };
          return { ...prev, people: [...current, id] };
      });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-30" onClick={onClose} />
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }} 
          animate={{ opacity: 1, scale: 1 }}
          className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6 dark:bg-gray-800"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {memory ? 'Erinnerung bearbeiten' : 'Neue Erinnerung'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-white">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Titel *</label>
              <input
                type="text"
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Datum</label>
                  <div className="relative">
                    <SafeIcon icon={FiCalendar} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <input
                      type="date"
                      value={formData.date}
                      onChange={e => setFormData({ ...formData, date: e.target.value })}
                      className="w-full pl-10 border border-gray-300 rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>
                <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Tags (Komma getrennt)</label>
                   <input
                      type="text"
                      value={formData.tags}
                      onChange={e => setFormData({ ...formData, tags: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Urlaub, Geburtstag..."
                   />
                </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Bild</label>
              <div className="flex items-center space-x-2">
                  <div className="relative flex-1">
                    <SafeIcon icon={FiImage} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                    <input
                      type="url"
                      value={formData.imageUrl}
                      onChange={e => setFormData({ ...formData, imageUrl: e.target.value })}
                      className="w-full pl-10 border border-gray-300 rounded-lg px-3 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="https://..."
                    />
                  </div>
                  <label className="cursor-pointer bg-gray-100 p-2 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600">
                      <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} disabled={uploading} />
                      {uploading ? <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin text-blue-600" /> : <SafeIcon icon={FiUpload} className="w-5 h-5 text-gray-600 dark:text-gray-300" />}
                  </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">Personen markieren</label>
              <div className="flex flex-wrap gap-2">
                  {familyMembers.map(member => (
                      <button
                        key={member.id}
                        type="button"
                        onClick={() => togglePerson(member.id)}
                        className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs transition-colors ${
                            formData.people?.includes(member.id) 
                                ? 'bg-blue-100 text-blue-800 border-blue-200 border dark:bg-blue-900/50 dark:text-blue-200' 
                                : 'bg-gray-50 text-gray-600 border-gray-200 border hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600'
                        }`}
                      >
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: member.color }}></div>
                          <span>{member.name}</span>
                      </button>
                  ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Beschreibung</label>
              <textarea
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 h-24 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg dark:bg-gray-700 dark:text-gray-300"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Speichern
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}